import React, { useCallback, useEffect, useState } from 'react';
import KitRequestModal from '../../pages/AiKit/KitRequestModal';
import { trackConversion, trackEvent } from '../../lib/analytics';
import {
  hasRequestedKit,
  isOtherDialogOpen,
  markKitRequested,
  markPopupShown,
  popupAlreadyShown,
  submitKitRequest,
} from '../../lib/aiKit';

/**
 * Popup de captación de la home: alta en la NEWSLETTER, que se abre solo al hacer
 * scroll. Aquí NO hay kit.
 *
 * Reutiliza el formulario de /resources/ai-kit (`KitRequestModal`) con
 * `variant="newsletter"`: mismo diseño, mismo campo único, misma casilla
 * obligatoria de Términos; cambian los textos, desaparece la casilla opcional de
 * newsletter (suscribirse ya es eso) y la confirmación no habla de kit ni de
 * oferta.
 *
 * Manda a `lead-magnet-html-form` con su propio prov_id y `source:
 * newsletter-web` (LEAD_FORMS.home). Ese endpoint SOLO crea el lead: no envía
 * correo ni devuelve contenido, así que la confirmación no promete ningún correo.
 *
 * Reglas de apertura (compartidas con la landing, mismas claves de sesión):
 *   - una sola vez por sesión, y nunca después de haberlo cerrado;
 *   - nunca a quien ya ha dejado su email, en la home o en la landing;
 *   - no se abre encima de otro diálogo (el chat de la web incluido).
 */

// Salta cuando el visitante ha BAJADO dos pantallas y ha dejado atrás la sección
// `after` (en la home, el mensaje principal).
//   - En pantallas y no en % de la página: con un 30 % la home (15.000 px en
//     escritorio, 21.000 en móvil) no saltaba hasta los 4.500 / 6.300 px y casi
//     nadie llegaba.
//   - Se cuenta desde el punto MÁS ALTO visto, no desde la posición absoluta. Al
//     llegar desde otra página ya bajada, ScrollTop sube con scroll suave y cada
//     fotograma de esa animación es un evento de scroll a 4.000 px: el popup
//     saltaba solo, sin que el visitante hiciera nada, y gastaba su única
//     aparición de la sesión. Igual en la web prerenderizada si se bajaba antes
//     de que llegara el JS.
//   - `after` cubre las pantallas bajas (360x640, móvil apaisado), donde dos
//     pantallas se quedan a mitad del mensaje principal.
//   - Y solo cuenta el scroll que hace el visitante: hasta su primera rueda,
//     toque, tecla o clic en la home, el punto de partida sigue a la página. Al
//     volver atrás o recargar, el navegador restaura la posición bajando con
//     scroll suave desde 0, y sin esto el popup saltaba solo.
const SCROLL_TRIGGER_SCREENS = 2;
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'touchmove', 'keydown', 'pointerdown'];

const HomeNewsletterPopup = ({ after }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (hasRequestedKit() || popupAlreadyShown()) return undefined;

    let done = false;
    let highest = window.scrollY;
    let userInput = false;
    const onUserInput = () => { userInput = true; };

    const pastSection = () => {
      const el = after ? document.querySelector(after) : null;
      return !el || el.getBoundingClientRect().bottom <= 0;
    };

    const onScroll = () => {
      if (done) return;
      if (!userInput) { highest = window.scrollY; return; }
      highest = Math.min(highest, window.scrollY);
      if (window.scrollY - highest < window.innerHeight * SCROLL_TRIGGER_SCREENS) return;
      if (!pastSection()) return;
      // Pudo cambiar mientras tanto (lo abrió o dejó su email en la landing sin
      // recargar, navegando dentro de la web): se vuelve a comprobar aquí.
      if (hasRequestedKit() || popupAlreadyShown()) { done = true; return; }
      // Otro diálogo abierto: se espera al siguiente scroll.
      if (isOtherDialogOpen()) return;
      done = true;
      markPopupShown();
      setOpen(true);
      trackEvent('Engagement', 'Home Newsletter Popup Auto', '');
    };

    USER_INPUT_EVENTS.forEach((type) => (
      window.addEventListener(type, onUserInput, { passive: true, capture: true })
    ));
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      USER_INPUT_EVENTS.forEach((type) => window.removeEventListener(type, onUserInput, { capture: true }));
      window.removeEventListener('scroll', onScroll);
    };
  }, [after]);

  const handleClose = useCallback(() => {
    markPopupShown();
    setOpen(false);
  }, []);

  const handleSubmit = useCallback(async (values) => {
    const res = await submitKitRequest({ ...values, form: 'home' });
    if (res && res.ok) {
      markKitRequested();
      // Solo el alta aceptada por el servidor. Ni el email va a analítica.
      trackConversion('Home Newsletter Signup');
    }
    return res;
  }, []);

  return (
    <KitRequestModal
      variant="newsletter"
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      returnFocusTo={null}
    />
  );
};

export default HomeNewsletterPopup;
